import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, AlertCircle, ArrowRight, CheckCircle2, Terminal, ShieldCheck, Activity, BarChart2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

export const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const location = useLocation();
  const { login, user } = useAuth();
  const navigate = useNavigate();

  // Toast de verificação bem-sucedida
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('verified') === 'true') {
      toast.success('Conta verificada com sucesso! Faça login para continuar.');
    }
  }, [location]);

  // Redirecionamento automático quando o user mudar no contexto
  useEffect(() => {
    if (user) {
      const roleName = user.role?.roleName || user.role;

      if (roleName === 'referralPartner') {
        navigate('/referral/dashboard', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password, rememberMe);
      toast.success('Bem-vindo de volta!');
    } catch (err: any) {
      let msg = 'Falha no login. Tente novamente.';

      if (err.response?.data?.message) {
        msg = err.response.data.message;

        if (err.response.data.errorType === 'user_not_found') {
          msg = 'Não encontramos nenhuma conta com este email. Quer criar uma?';
        }
      } else if (err.message) {
        msg = err.message;
      }

      if (msg.includes('No refresh token available')) {
        msg = 'Falha no login. Verifique suas credenciais.';
      }

      if (err.response?.status === 403 && msg.includes('não verificada')) {
        msg += ' Verifique o seu email para ativar a conta.';
      }

      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#0B0C1E] text-[#e2e8f0] font-sans antialiased selection:bg-[#52C47C] selection:text-[#0B0C1E] flex flex-col lg:flex-row overflow-x-hidden relative">
      
      {/* Structural Grid Lines */}
      <div className="fixed inset-0 pointer-events-none z-0 flex justify-between px-4 sm:px-12 opacity-[0.03]">
        <div className="w-[1px] h-full bg-white" />
        <div className="w-[1px] h-full bg-white" />
        <div className="w-[1px] h-full bg-white" />
        <div className="w-[1px] h-full bg-white" />
      </div>

      {/* Glow futurista */}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[#52C47C]/5 blur-[160px] pointer-events-none" />

      {/* Coluna Esquerda: Branding & Informação Institucional (Desktop) */}
      <div className="hidden lg:flex lg:w-1/2 relative flex-col justify-between p-12 xl:p-16 border-r border-white/10 z-10">
        
        <div>
          <Link to="/" className="inline-flex items-center gap-3 group">
            <img 
              src="https://meupontodevenda.com/cdn/logo-white.svg" 
              alt="MPV — Meu Ponto de Venda" 
              className="h-7 w-auto object-contain transition-transform group-hover:scale-95"
            />
            <span className="font-mono text-[10px] tracking-widest text-[#8087A8] border-l border-white/10 pl-3">
              SISTEMA v4.0
            </span>
          </Link>
        </div>

        <div className="max-w-lg my-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 border border-[#52C47C]/30 bg-[#52C47C]/10 font-mono text-[11px] text-[#52C47C] tracking-widest uppercase mb-6">
            <Terminal className="w-3.5 h-3.5" />
            <span>ACESSO CORPORATIVO</span>
          </div>

          <motion.h2 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl xl:text-5xl font-light text-white leading-[1.1] mb-6 tracking-tight"
          >
            A sua empresa vende <span className="font-normal italic text-[#52C47C]">O MPV organiza o resto.</span>
          </motion.h2>

          <p className="text-slate-300 text-sm font-light leading-relaxed mb-10">
           Aceda o seu MPV — conecta leads, clientes, vendas, stock, facturação e rentabilidade numa única plataforma, criada para a realidade das empresas moçambicanas.
          </p>

          <div className="space-y-4 font-mono">
            {[
              { 
                code: "MOD-POS", 
                title: "POS & Faturação Fiscal", 
                desc: "Emissão de documentos comerciais em 3 segundos com M-Pesa integrado." 
              },
              { 
                code: "MOD-CRM", 
                title: "Pipeline & Metas Comerciais", 
                desc: "Acompanhamento de metas por equipa e cálculo automático de comissões." 
              },
              { 
                code: "MOD-ECO", 
                title: "Unit Economics & Margens", 
                desc: "Visibilidade em tempo real da rentabilidade real por produto e cliente." 
              }
            ].map((item, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, x: -15 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 + idx * 0.1 }}
                className="p-4 bg-[#12142B] border border-white/10 flex items-start gap-4"
              >
                <span className="text-[10px] text-[#52C47C] border border-[#52C47C]/30 px-2 py-0.5 mt-0.5 shrink-0">
                  {item.code}
                </span>
                <div>
                  <h4 className="text-white text-xs font-bold uppercase tracking-wider">{item.title}</h4>
                  <p className="text-slate-400 text-xs font-light font-sans mt-0.5 leading-relaxed">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="pt-8 border-t border-white/10 font-mono text-[10px] text-slate-500 tracking-wider flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#52C47C]" />
            <span>SISTEMA OPERACIONAL COMERCIAL MOÇAMBIQUE</span>
          </div>
          <span>© {new Date().getFullYear()} MPV</span>
        </div>

      </div>

      {/* Coluna Direita: Formulário de Login */}
      <div className="flex-1 flex flex-col justify-between p-6 sm:p-12 relative z-10 min-h-screen">
        
        {/* Mobile Header Logo */}
        <div className="lg:hidden flex items-center justify-between pb-8 border-b border-white/10 mb-6">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="https://meupontodevenda.com/cdn/logo-white.svg" 
              alt="MPV — Meu Ponto de Venda" 
              className="h-6 w-auto object-contain"
            />
          </Link>
          <span className="font-mono text-[10px] text-[#52C47C] border border-[#52C47C]/30 px-2 py-0.5 uppercase">
            v4.0
          </span>
        </div>

        <div className="my-auto flex items-center justify-center">
          <motion.div 
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-md"
          >
            {/* Card Form */}
            <div className="bg-[#12142B] border border-white/10 p-8 sm:p-10 shadow-2xl relative">
              
              {/* Header do Card */}
              <div className="mb-8 text-left">
                <span className="font-mono text-[10px] text-[#52C47C] tracking-widest uppercase block mb-2">
                  // AUTENTICAÇÃO SEGURA
                </span>
                <h1 className="text-3xl font-light text-white tracking-tight mb-2">
                  Acesso ao Sistema
                </h1>
                <p className="text-slate-400 text-xs font-light">
                  Introduza as suas credenciais para aceder ao ambiente da sua empresa.
                </p>
              </div>

              {error && (
                <motion.div 
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-red-500/10 border border-red-500/30 p-4 mb-6 flex items-start gap-3 text-red-300 font-mono text-xs"
                >
                  <AlertCircle className="h-4 w-4 text-red-400 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p>{error}</p>
                    {error.includes('nenhuma conta') && (
                      <Link to="/register" className="text-[#52C47C] underline font-bold mt-2 block uppercase">
                        Criar conta agora →
                      </Link>
                    )}
                  </div>
                </motion.div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-4">
                  <div>
                    <label className="block font-mono text-xs text-slate-400 uppercase tracking-wider mb-2">
                      E-mail de Acesso
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                        <Mail className="h-4 w-4 text-slate-500" />
                      </div>
                      <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="block w-full pl-10 pr-4 py-3 bg-[#0B0C1E] border border-white/10 text-white placeholder-slate-600 focus:border-[#52C47C] focus:ring-1 focus:ring-[#52C47C]/30 transition-all outline-none font-sans text-sm"
                        placeholder="exemplo@empresa.co.mz"
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="font-mono text-xs text-slate-400 uppercase tracking-wider">
                        Palavra-passe
                      </label>
                      <Link to="/forgot-password" className="font-mono text-[11px] text-[#52C47C] hover:underline">
                        Esqueceu a senha?
                      </Link>
                    </div>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                        <Lock className="h-4 w-4 text-slate-500" />
                      </div>
                      <input
                        type="password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="block w-full pl-10 pr-4 py-3 bg-[#0B0C1E] border border-white/10 text-white placeholder-slate-600 focus:border-[#52C47C] focus:ring-1 focus:ring-[#52C47C]/30 transition-all outline-none font-sans text-sm"
                        placeholder="••••••••"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex items-center pt-1">
                  <label className="flex items-center cursor-pointer group select-none">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        className="sr-only"
                      />
                      <div className={`w-4 h-4 border transition-all flex items-center justify-center ${
                        rememberMe ? 'bg-[#52C47C] border-[#52C47C]' : 'bg-[#0B0C1E] border-white/20 group-hover:border-white/40'
                      }`}>
                        {rememberMe && <CheckCircle2 className="w-3.5 h-3.5 text-[#0B0C1E]" />}
                      </div>
                    </div>
                    <span className="ml-2.5 font-mono text-xs text-slate-400 group-hover:text-slate-300 transition-colors">
                      Manter sessão iniciada
                    </span>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3.5 px-4 bg-[#52C47C] hover:bg-white text-[#0B0C1E] font-mono font-bold text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-2 group shadow-lg shadow-[#52C47C]/15 disabled:opacity-50 mt-6"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#0B0C1E] border-t-transparent"></div>
                  ) : (
                    <>
                      <span>Entrar no Sistema</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </button>
              </form>

              <div className="mt-8 pt-6 border-t border-white/10 text-center font-mono text-xs text-slate-400">
                <p>
                  Ainda não tem conta?{' '}
                  <Link 
                    to="/register" 
                    className="text-[#52C47C] font-bold hover:underline ml-1 uppercase"
                  >
                    Registar Empresa →
                  </Link>
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Mobile Footer */}
        <div className="lg:hidden text-center font-mono text-[10px] text-slate-500 pt-6">
          © {new Date().getFullYear()} MPV — MEU PONTO DE VENDA
        </div>

      </div>

    </div>
  );
};

export default Login;